import logEvent from 'analytics/logEvent';
import { getTariff } from 'domains/products/products.utils';
import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import { useRouter } from 'next/router';
import React from 'react';

import FormattedPrice from 'components/basic/FormattedPrice';
import { H3 } from 'components/basic/Typography';
import Description from 'components/complex/payments/Product/Description';
import DiscountLabel from 'components/complex/payments/Product/DiscountLabel';
import Price from 'components/complex/payments/Product/Price';
import ProductWrapper from 'components/complex/payments/Product/ProductWrapper';

import type { ProductType } from 'redux-store/slices/products/types';

import s from './Product.module.scss';

interface Props {
  product: ProductType;
  setSelectedPlanId?: (id: number) => void;
  isSelected?: boolean;
  isCurrentUserPlan?: boolean;
  nextPayment?: string;
  className?: string;
  isRu?: boolean;
}

export default function Product({
  product,
  isSelected,
  setSelectedPlanId,
  isCurrentUserPlan,
  nextPayment,
  className,
  isRu,
}: Props): JSX.Element {
  const { discount, interval, interval_count, discount_price, price, currency } = product.prices[0];
  const { id, title } = product;
  const router = useRouter();
  const coupon = router.query.coupon as string;
  const t = useTranslationAndTypograph();
  const isShowDiscount = !isCurrentUserPlan && !!discount;
  const showedPrice = isShowDiscount ? discount_price : price;

  let months = 1;

  if (interval === 'year') {
    months = 12;
  }

  if (interval === '2-year') {
    months = 24;
  }

  const isShowingOriginalPrice =
    interval !== 'lifetime' &&
    interval !== 'month' &&
    interval !== 'week' &&
    interval !== 'year-gift' &&
    showedPrice &&
    !isCurrentUserPlan &&
    !isRu;

  return (
    <ProductWrapper
      isSelected={isSelected}
      isCurrentUserPlan={isCurrentUserPlan}
    >
      <div
        onClick={(): void => {
          if (isCurrentUserPlan) {
            return;
          }

          logEvent('Web_Payment_Tariff_Changed', {
            tariff: getTariff(product),
            duration: interval_count,
            coupon,
            'coupon%': discount,
          });
          if (!isCurrentUserPlan && setSelectedPlanId) {
            setSelectedPlanId(id);
          }
        }}
        className={cn(s.wrapper, className, `cy-${interval}-plan`, {
          [s.selected]: isSelected,
          [s.currentPlan]: isCurrentUserPlan,
        })}
      >
        {isShowDiscount && <DiscountLabel product={product} />}
        <div className={s.titleWrapper}>
          <H3 Tag="div" className={s.title}>
            {t(title)}
          </H3>
          {isCurrentUserPlan ? null : (
            <Price
              product={product}
              isShowDiscount={isShowDiscount}
              isCurrentUserPlan={isCurrentUserPlan}
            />
          )}
        </div>

        <div className={s.secondLineWrapper}>
          <Description
            product={product}
            isCurrentUserPlan={isCurrentUserPlan}
            nextPayment={nextPayment}
            isRu={isRu}
          />
          {!isCurrentUserPlan && isShowingOriginalPrice && (
            <H3 className={s.monthPrice}>
              <FormattedPrice
                price={(+showedPrice / months).toFixed(2)}
                currency={currency}
              />
              /{t('month')}
            </H3>
          )}
        </div>
      </div>
    </ProductWrapper>
  );
}
